/**
 * Searchspring overlay for the search page input.
 * When the user types in the search bar on /search, shows an overlay with
 * suggestions (Searchspring Suggest API). Mobile: white overlay; desktop: dropdown (4 suggestions).
 */

/* global SearchspringAPI */

import { debounce } from '@theme/utilities';

const DESKTOP_SUGGESTIONS_LIMIT = 4;
const SUGGEST_DEBOUNCE_MS = 300;
const BLUR_HIDE_DELAY_MS = 200;

/**
 * @param {unknown} data - Raw getSuggestions response
 * @param {string} currentQuery - Current input value
 * @param {string} searchUrl - Base search URL
 * @returns {{ text: string, url: string }[]}
 */
function formatSuggestions(data, currentQuery, searchUrl) {
  /** @type {{ text: string, url: string }[]} */
  const suggestions = [];
  if (!data || typeof data !== 'object') return suggestions;

  const raw =
    /** @type {{ suggested?: { text: string }, alternatives?: Array<{ text: string }> }} */ (data);

  if (raw.suggested?.text && raw.suggested.text !== currentQuery) {
    suggestions.push({
      text: raw.suggested.text,
      url: `${searchUrl}?type=product&q=${encodeURIComponent(raw.suggested.text)}`,
    });
  }

  if (raw.alternatives?.length) {
    raw.alternatives.slice(0, 5).forEach((alt) => {
      if (alt?.text && !suggestions.find((s) => s.text === alt.text)) {
        suggestions.push({
          text: alt.text,
          url: `${searchUrl}?type=product&q=${encodeURIComponent(alt.text)}`,
        });
      }
    });
  }

  return suggestions;
}

function isDesktop() {
  return typeof window !== 'undefined' && window.matchMedia('(min-width: 1080px)').matches;
}

/**
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Highlight query match in text (same as predictive search).
 * Escapes text first, then wraps matching query in <strong>.
 * @param {string} text
 * @param {string} query
 * @returns {string} HTML string
 */
function highlightMatch(text, query) {
  const escapedText = escapeHtml(text);
  if (!query || !text) return escapedText;
  const escapedQuery = escapeHtml(query);
  if (!escapedQuery) return escapedText;
  const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return escapedText.replace(regex, '<strong>$1</strong>');
}

function init() {
  if (typeof SearchspringAPI === 'undefined' || !SearchspringAPI?.isEnabled()) {
    return;
  }

  const overlay = document.querySelector('.search-page-input__overlay');
  if (!overlay || !(overlay instanceof HTMLElement)) return;

  const inputId = overlay.getAttribute('data-search-input-id');
  const input = inputId
    ? document.getElementById(inputId)
    : overlay.closest('form')?.querySelector('input[name="q"]');
  if (!input || !(input instanceof HTMLInputElement)) return;

  const list = overlay.querySelector('[data-ss-suggestions-list]');
  if (!list || !(list instanceof HTMLElement)) return;

  const config = SearchspringAPI.getConfig();
  const searchUrl = config?.routes?.search || '/search';

  /** @type {ReturnType<typeof setTimeout> | null} */
  let hideTimeout = null;

  /** @type {HTMLElement} */
  const overlayEl = overlay;
  /** @type {HTMLInputElement} */
  const inputEl = input;
  /** @type {HTMLElement} */
  const listEl = list;

  function showOverlay() {
    overlayEl.hidden = false;
    inputEl.setAttribute('aria-expanded', 'true');
    if (!isDesktop()) {
      document.documentElement.setAttribute('scroll-lock', '');
    }
  }

  function hideOverlay() {
    overlayEl.hidden = true;
    inputEl.setAttribute('aria-expanded', 'false');
    if (!isDesktop()) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  function renderSuggestions(/** @type {{ text: string, url: string }[]} */ items) {
    const limit = isDesktop() ? DESKTOP_SUGGESTIONS_LIMIT : items.length;
    const toShow = items.slice(0, limit);

    if (toShow.length === 0) {
      hideOverlay();
      return;
    }

    const query = inputEl.value.trim();
    listEl.innerHTML = toShow
      .map(
        (s, index) =>
          `<li
            class="ss-predictive-search__suggestion-item"
            role="option"
            data-query="${escapeHtml(s.text)}"
            data-index="${index}"
          >
            <a href="${escapeHtml(s.url)}" class="ss-predictive-search__suggestion-link" data-cnstrc-item-section="Search Suggestions" data-cnstrc-item-name="${escapeHtml(s.text)}" data-cnstrc-item-id="${escapeHtml(s.text)}" data-suggestion>${highlightMatch(s.text, query)}</a>
          </li>`,
      )
      .join('');

    listEl.querySelectorAll('.ss-predictive-search__suggestion-link').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const href = link.getAttribute('href');
        if (href) window.location.href = href;
      });
    });

    showOverlay();
  }

  const fetchSuggestions = debounce(async () => {
    const query = inputEl.value.trim();
    if (!query) {
      hideOverlay();
      return;
    }

    try {
      const data = await SearchspringAPI.getSuggestions(query);
      const suggestions = formatSuggestions(data, query, searchUrl);
      renderSuggestions(suggestions);
    } catch (err) {
      if (err?.name !== 'AbortError')
        console.error('[Searchspring] Search page suggest error:', err);
      hideOverlay();
    }
  }, SUGGEST_DEBOUNCE_MS);

  inputEl.addEventListener('input', () => {
    if (!inputEl.value.trim()) {
      hideOverlay();
      return;
    }
    fetchSuggestions();
  });

  inputEl.addEventListener('focus', () => {
    if (inputEl.value.trim() && listEl.children.length > 0) showOverlay();
  });

  inputEl.addEventListener('blur', () => {
    hideTimeout = window.setTimeout(() => {
      hideOverlay();
      hideTimeout = null;
    }, BLUR_HIDE_DELAY_MS);
  });

  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hideOverlay();
      inputEl.blur();
    }
  });

  overlayEl.addEventListener('mousedown', (_e) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  });

  document.addEventListener('click', (e) => {
    if (overlayEl.hidden) return;
    if (!overlayEl.contains(/** @type {Node} */ (e.target)) && e.target !== inputEl) {
      hideOverlay();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
